import * as React from "react";
import Cards from "@amzn/awsui-components-react/polaris/cards";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  setAsinTypeState,
  setPartyState,
  setProgramState,
  setStampSelected,
  setWeblabSelected,
  unsetStampSelected,
  unsetWeblabSelected,
} from "./stamperWizardConnector";
import { FormField } from "@amzn/awsui-components-react";
import Select from "@amzn/awsui-components-react/polaris/select";
import RadioGroup from "@amzn/awsui-components-react/polaris/radio-group";
import {
  ASIN_PARTIES,
  LAUNCH_ASIN_TYPES,
} from "../../constants/launchAsinTypes";
import { OptionDefinition } from "./StamperWizard";
import { useEffect } from "react";
import { fetchEditableAttributes } from "../porAsinList/porAsinListSlice";
import { useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import { AuthorizedAttribute } from "../../server/brass/model";

type StamperOptionsProps = {
  dispatch: any;
  touched: { [key: string]: boolean };
  weblabSelected: boolean;
  stampSelected: boolean;
  programs: OptionDefinition[];
  program?: string;
  programName?: string;
  partiness?: string;
  type?: string;
};

interface ItemDescription {
  name: string;
  description: string;
}

export const StamperOptions = ({
  dispatch,
  touched,
  weblabSelected,
  stampSelected,
  programs,
  program,
  programName,
  partiness,
  type,
}: StamperOptionsProps) => {
  const types = Object.entries(LAUNCH_ASIN_TYPES).map(
    ([serviceName, readableName]) => ({
      label: readableName,
      value: serviceName,
    })
  );
  const { editableAttributes, isGettingEditableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );

  useEffect(() => {
    if (!isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  });

  function userIsAuthorizedForStamp(editableAttributes: AuthorizedAttribute[]) {
    return editableAttributes.some(
      (attribute) =>
        attribute.attributeName === "publish_stamp" && attribute.authorized
    );
  }

  function userIsAuthorizedForWeblab(
    editableAttributes: AuthorizedAttribute[]
  ) {
    return editableAttributes.some(
      (attribute) =>
        attribute.attributeName === "publish_weblab" && attribute.authorized
    );
  }

  const STAMP_OPTION_NAME = "Stamp in Catalog";
  const WEBLAB_OPTION_NAME = "Stamp with Weblab";

  const STAMP_OPTION = {
    name: STAMP_OPTION_NAME,
    description:
      "We will generate a Stamp using the Program/ASIN Party/ASIN Type and publish it to the ASIN in " +
      "Catalog using CC API. This value is added to the program_participation attribute and the respective ASINs " +
      "will be ingested into Devices ASIN Central automatically once reconciled (in most cases, less than 30" +
      " minutes after stamping).",
  };
  const WEBLAB_OPTION = {
    name: WEBLAB_OPTION_NAME,
    description:
      "We will generate an unique Weblab string for the program name and write this into the G2S2 Table mako_dp_gating. " +
      "This will redirect external traffic from the ASIN/Marketplace detail page so customers cannot view it",
  };

  const optionItems = [];
  if (userIsAuthorizedForStamp(editableAttributes)) {
    optionItems.push(STAMP_OPTION);
  }
  if (userIsAuthorizedForWeblab(editableAttributes)) {
    optionItems.push(WEBLAB_OPTION);
  }

  const selectedItems: ItemDescription[] = [];
  if (stampSelected) {
    selectedItems.push(STAMP_OPTION);
  }
  if (weblabSelected) {
    selectedItems.push(WEBLAB_OPTION);
  }
  return (
    <div>
      <span>
        Select one or both options then select the required attributes
      </span>
      <FormField
        stretch={true}
        errorText={
          touched["2"] && !weblabSelected && !stampSelected
            ? "You must select at least one option"
            : undefined
        }
      >
        <Cards
          onSelectionChange={({ detail }) => {
            // setSelectedItems(detail?.selectedItems ?? [])
            if (
              detail?.selectedItems
                .map((item) => item.name)
                .includes(STAMP_OPTION_NAME)
            ) {
              dispatch(setStampSelected());
            } else {
              dispatch(unsetStampSelected());
            }
            if (
              detail?.selectedItems
                .map((item) => item.name)
                .includes(WEBLAB_OPTION_NAME)
            ) {
              dispatch(setWeblabSelected());
            } else {
              dispatch(unsetWeblabSelected());
            }
          }}
          selectedItems={selectedItems}
          cardDefinition={{
            header: (item) => item.name,
            sections: [
              {
                id: "description",
                header: "Description",
                content: (item) => item.description,
              },
            ],
          }}
          cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
          items={optionItems}
          loadingText="Loading resources"
          selectionType="multi"
          trackBy="name"
          visibleSections={["description", "type", "size"]}
        />
      </FormField>

      <SpaceBetween direction="vertical" size="l">
        {stampSelected || weblabSelected ? (
          <FormField
            id={"asin-form"}
            label="Program"
            stretch={true}
            errorText={
              touched["2"] && !program
                ? "Program selection is required"
                : undefined
            }
          >
            <Select
              selectedOption={{ label: programName, value: program }}
              onChange={({ detail }) => {
                // setProgram(detail.selectedOption)
                dispatch(
                  setProgramState({
                    program: detail.selectedOption.value,
                    programName: detail.selectedOption.label,
                  })
                );
              }}
              filteringType="auto"
              options={programs}
              selectedAriaLabel="Selected"
              invalid={touched["2"] && !program}
              errorText={
                touched["2"] && !program ? "A Program is required" : undefined
              }
            />
          </FormField>
        ) : (
          <></>
        )}
        {stampSelected ? (
          <FormField
            id={"asin-form"}
            label="ASIN Party"
            stretch={true}
            errorText={
              touched["2"] && !partiness
                ? "ASIN Party selection is required"
                : undefined
            }
          >
            <RadioGroup
              onChange={({ detail }) => {
                dispatch(setPartyState({ partiness: detail.value }));
              }}
              value={partiness || null}
              items={ASIN_PARTIES.map((p) => ({
                label: p,
                value: p,
              }))}
            />
          </FormField>
        ) : (
          <></>
        )}
        {stampSelected ? (
          <FormField
            id={"asin-form"}
            label="ASIN Type"
            stretch={true}
            errorText={
              touched["2"] && !type
                ? "ASIN Type selection is required"
                : undefined
            }
            constraintText={"Only one ASIN Type allowed per submission"}
          >
            <Select
              selectedOption={{
                label: LAUNCH_ASIN_TYPES[type || ""],
                value: type,
              }}
              onChange={({ detail }) => {
                dispatch(
                  setAsinTypeState({ type: detail.selectedOption.value })
                );
              }}
              filteringType="auto"
              options={types}
              selectedAriaLabel="Selected"
              invalid={touched["2"] && !type}
              errorText={
                touched["2"] && !type ? "ASIN Type is required" : undefined
              }
            />
          </FormField>
        ) : (
          <></>
        )}
      </SpaceBetween>
    </div>
  );
};
